import z from "zod";
import { addressModel } from "./core";
import {
  BorrowerProfile,
  CollateralType,
  CompanyStage,
  CompanyType,
  LoanApplicationViewModel,
  LoanType,
  RevenueMix,
  type YearProfitLossMetrics,
} from "@cxm/client";

// Mirrors YearProfitLossMetrics. Zod strips unknown keys at submit, so a metric
// absent here is dropped from the payload even when the adapter maps it.
export const yearProfitLossMetricsModel = z.object({
  year: z.number().nullish(),
  revenues: z.number().nullish(),
  costOfSales: z.number().nullish(),
  grossProfit: z.number().nullish(),
  sga: z.number().nullish(),
  ebitda: z.number().nullish(),
  depreciation: z.number().nullish(),
  amortization: z.number().nullish(),
  operatingIncome: z.number().nullish(),
  interestExpense: z.number().nullish(),
  totalOtherIncome: z.number().nullish(),
  totalOtherExpenses: z.number().nullish(),
  preTaxIncome: z.number().nullish(),
  taxes: z.number().nullish(),
  netIncome: z.number().nullish(),
});

// Fails to compile if this model and the generated YearProfitLossMetrics
// diverge in either direction. Zod silently strips a key that is on the
// generated type but missing here - the exact defect that left five borrower
// P&L metrics rendering, accepting input, and being dropped at submit before
// this model was extended to match the generated type.
type MissingFromYearProfitLossMetricsModel = Exclude<
  keyof YearProfitLossMetrics,
  keyof z.infer<typeof yearProfitLossMetricsModel>
>;
type ExtraInYearProfitLossMetricsModel = Exclude<
  keyof z.infer<typeof yearProfitLossMetricsModel>,
  keyof YearProfitLossMetrics
>;
const _yearProfitLossMetricsModelMatchesGeneratedType: MissingFromYearProfitLossMetricsModel extends never
  ? ExtraInYearProfitLossMetricsModel extends never
    ? true
    : never
  : never = true;
void _yearProfitLossMetricsModelMatchesGeneratedType;

export const borrowerCashFlowMetricsModel = z.object({
  asOfDate: z.string().nullish(),
  sourceEntity: z.string().nullish(),
  sourceFileId: z.number().nullish(),
  netIncome: z.number().nullish(),
  depreciationAmortization: z.number().nullish(),
  changeInAccountsReceivable: z.number().nullish(),
  changeInInventory: z.number().nullish(),
  changeInAccountsPayable: z.number().nullish(),
  changeInAccruedLiabilities: z.number().nullish(),
  operatingOther: z.number().nullish(),
  netCashOperating: z.number().nullish(),
  cashFromSaleOfCapitalAssets: z.number().nullish(),
  cashPaidForCapitalAssets: z.number().nullish(),
  investingOther: z.number().nullish(),
  netCashInvesting: z.number().nullish(),
  cashFromBorrowings: z.number().nullish(),
  cashPaidToRetireDebt: z.number().nullish(),
  ownerDistributions: z.number().nullish(),
  ownerContributions: z.number().nullish(),
  financingOther: z.number().nullish(),
  netCashFinancing: z.number().nullish(),
  netChangeInCash: z.number().nullish(),
});

export const getFullFileList = (borrowerProfile: BorrowerProfile) => {
  return [
    borrowerProfile.companyInformation?.orgChartFile,
    borrowerProfile.companyInformation?.businessPlanFile,
    ...(borrowerProfile.companyInformation?.customerContractFiles ??
      []),
    ...(borrowerProfile.taxInformation?.taxReturnFiles ?? []),
    ...(borrowerProfile.taxInformation?.lastYearBankStatementFiles ??
      []),
    ...(borrowerProfile.profitLossMetrics?.previousYearsFiles ?? []),
    ...(borrowerProfile.profitLossMetrics
      ?.currentAndNextYearProjectionFiles ?? []),
    borrowerProfile.balanceSheetMetrics?.mostRecentBalanceSheetFile,
    ...(borrowerProfile.balanceSheetMetrics
      ?.previousYearsBalanceSheetFiles ?? []),
    ...(borrowerProfile.balanceSheetMetrics
      ?.accountsReceivableScheduleFiles ?? []),
    ...(borrowerProfile.balanceSheetMetrics
      ?.accountsPayableScheduleFiles ?? []),
    ...(borrowerProfile.balanceSheetMetrics?.inventoryBreakdownFiles ??
      []),
    ...(borrowerProfile.balanceSheetMetrics?.inventorySupportingFiles ??
      []),
    ...(borrowerProfile.balanceSheetMetrics?.equipmentBreakdownFiles ??
      []),
    ...(borrowerProfile.balanceSheetMetrics
      ?.equipmentExternalAppraisalFiles ?? []),
    ...(borrowerProfile.balanceSheetMetrics?.realEstateBreakdownFiles ??
      []),
    ...(borrowerProfile.balanceSheetMetrics
      ?.realEstateExternalAppraisalFiles ?? []),
    ...(borrowerProfile.balanceSheetMetrics
      ?.additionalDebtScheduleFiles ?? []),
  ];
};

export const companyTypeOptions = [
  {
    label: "LLC",
    value: CompanyType.LLC,
  },
  {
    label: "S-Corp",
    value: CompanyType.SCORP,
  },
  {
    label: "C-Corp",
    value: CompanyType.CCORP,
  },
  {
    label: "Partnership",
    value: CompanyType.PARTNERSHIP,
  },
  {
    label: "Sole Proprietorship",
    value: CompanyType.SOLE_PROPRIETORSHIP,
  },
];

export const debtCollateralModel = z.object({
  collateralType: z.nativeEnum(CollateralType).optional(),
  initialValue: z.number().optional(),
  currentValue: z.number().optional(),
  lastAppraisalDate: z.number().optional(),
});

export const additionalDebtModel = z.object({
  debtType: z.nativeEnum(LoanType).optional(),
  lender: z.string().optional(),
  position: z.number().min(1).max(10).optional(),
  initialAmount: z.number().optional(),
  remainingAmount: z.number().optional(),
  monthlyPayment: z.number().optional(),
  maturityDate: z.number().optional(),
  interestRate: z.number().optional(),
  collateral: debtCollateralModel.array().optional(),
});

export const contactModel = z.object({
  firstName: z.string(),
  lastName: z.string(),
  title: z.string(),
  email: z.string().email(),
  phoneNumber: z.string(),
});

export const officerModel = z.object({
  firstName: z.string(),
  lastName: z.string(),
  title: z.string(),
  email: z.string(),
  authorizedSignatory: z.boolean().nullish(),
  phoneNumber: z.string(),
  percentageShare: z.number().min(0).max(100),
  socialSecurityNumber: z.string().min(9, "Must be 9 digits"),
  driversLicenseFile: z.string().nullish(),
});

export const fullBorrowerProfileModel = z.object({
  companyInformation: z.object({
    companyName: z.string().optional(),
    stage: z.nativeEnum(CompanyStage).optional(),
    hasMoreThan3MonthsRunway: z.boolean().optional(),
    companyWebsite: z.string().optional(),
    companyPhone: z.string().optional(),
    yearFormed: z.number().max((new Date()).getFullYear()).min(1500).optional(),
    companyType: z.nativeEnum(CompanyType).optional(),
    headQuartersAddress: addressModel.optional(),
    businessDescription: z.string().optional(),
    numberOfEmployees: z.string().optional(),
    industry: z.string().optional(),
    subIndustry: z.string().optional(),
    employerIdentificationNumber: z.string().optional(),
    veteranOwned: z.boolean().optional(),
    femaleOwned: z.boolean().optional(),
    disabilityOwned: z.boolean().optional(),
    minorityOwned: z.boolean().optional(),
    businessPlanFiles: z.string().array().optional(),
    orgChartFiles: z.string().array().optional(),
    growthPlansDescription: z.string().optional(),
    primaryContact: contactModel.optional(),
    secondaryContacts: contactModel.array().optional(),
    officers: officerModel.array().optional(),
    nonManagementShareholdersOverTenPercent: z.boolean().optional(),
    revenueMix: z.nativeEnum(RevenueMix).array().optional(),
    totalRevenuePercentOfTopFiveCustomers: z.number().optional(),
    customerContractFiles: z.string().array().optional(),
    articlesOfIncorporationFiles: z.string().array().optional(),
    operatingAgreementFiles: z.string().array().optional(),
    licensesFiles: z.string().array().optional(),
    personalFinancialStatementsFiles: z.string().array().optional(),
    insuranceFiles: z.string().array().optional(),
    dunsNumber: z.string().min(9, "Must be 9 digits").max(9, "Must be 9 digits").optional(),
    uniqueEntityIdentifier: z.string().min(12, "Must be 12 digits").max(12, "Must be 12 digits").optional(),
    cageCode: z.string().min(5, "Must be 5 digits").max(5, "Must be 5 digits").optional(),
  }).optional(),
  taxInformation: z.object({
    fullTimeCFO: z.boolean().optional(),
    outsideAccountant: z.boolean().optional(),
    taxReturnFiles: z.string().array().optional(),
    lastYearBankStatementFiles: z.string().array().optional(),
  }).optional(),
  profitLossMetrics: z.object({
    monthlyRecurringRevenue: z.number().optional(),
    annualChurn: z.number().optional(),
    fiscalYearEnd: z.number().optional(),
    yearlyDebtService: z.number().optional(),
    previousYearsFiles: z.string().array().optional(),
    previousYearsMetrics: yearProfitLossMetricsModel.array().optional(),
    ttmMetrics: yearProfitLossMetricsModel.optional(),
    ytdMetrics: yearProfitLossMetricsModel.optional(),
    currentAndNextYearProjectionFiles: z.string().array().optional(),
    forecastedYearsMetrics: yearProfitLossMetricsModel.array()
      .optional(),
  }).optional(),
  balanceSheetMetrics: z.object({
    mostRecentBalanceSheetFiles: z.string().array().optional(),
    previousYearsBalanceSheetFiles: z.string().array().optional(),
    balanceSheetAsOfDate: z.number().optional(),
    currentCash: z.number().optional(),
    currentAssets: z.number().optional(),
    totalAssets: z.number().optional(),
    totalUnencumberedAssets: z.number().optional(),
    currentLiabilities: z.number().optional(),
    totalLiabilities: z.number().optional(),
    accountsReceivableScheduleFiles: z.string().array().optional(),
    accountsReceivableTotal: z.number().optional(),
    accountsReceivable30Days: z.number().optional(),
    accountsReceivable3160Days: z.number().optional(),
    accountsReceivable6190Days: z.number().optional(),
    accountsReceivable90PlusDays: z.number().optional(),
    inventoryOver500k: z.boolean().optional(),
    inventoryAmount: z.number().optional(),
    inventorySupportingFiles: z.string().array().optional(),
    inventoryBreakdownFiles: z.string().array().optional(),
    equipmentOver500k: z.boolean().optional(),
    estEquipmentValue: z.number().optional(),
    equipmentBreakdownFiles: z.string().array().optional(),
    equipmentExternalAppraisalFiles: z.string().array().optional(),
    hasEquipmentDebt: z.boolean().optional(),
    totalDebtSecuredByEquipment: z.number().optional(),
    realEstateOver500k: z.boolean().optional(),
    estRealEstateValue: z.number().optional(),
    realEstateBreakdownFiles: z.string().array().optional(),
    realEstateExternalAppraisalFiles: z.string().array().optional(),
    hasRealEstateDebt: z.boolean().optional(),
    totalMortgages: z.number().optional(),
    accountsPayableScheduleFiles: z.string().array().optional(),
    accountsPayableTotal: z.number().optional(),
    accountsPayable30Days: z.number().optional(),
    accountsPayable3160Days: z.number().optional(),
    accountsPayable6190Days: z.number().optional(),
    accountsPayable90PlusDays: z.number().optional(),
    hasAdditionalDebt: z.boolean().optional(),
    totalAdditionalDebt: z.number().optional(),
    additionalDebtScheduleFiles: z.string().array().optional(),
    additionalDebt: additionalDebtModel.array().optional(),
    balanceSheetDetail: z.any().nullable().optional(),
  }).optional(),
  cashFlowMetrics: borrowerCashFlowMetricsModel.optional(),
  additionalDocs: z.string().array().optional(),
});
